import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPagination } from '@mui/x-data-grid';

import { AdAssetViewItem, AdGroupListItem, getAdAssetView } from '~/services/PublicOfferFeedService';

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

export default function AdCreativeAIAssistant({
  campaignId,
  adGroupId,
  customerId,
  adGroup,
}: {
  campaignId: string;
  adGroupId: string;
  customerId: string;
  adGroup?: AdGroupListItem;
}) {
  const [adAssetViewItems, setAdAssetViewItems] = useState<Array<AdAssetViewItem>>([]);
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [aiSelections, setAiSelections] = useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();

  const fetchAdAssetView = useCallback(
    async (customerId: string, campaignId: string, adGroupId: string) => {
      try {
        setFetchingState('loading');
        const response = await getAdAssetView(customerId, campaignId, adGroupId);
        const itemsWithId = (response.result || []).map((item) => ({
          ...item,
          id: `${item.adGroupAdId}-${item.assetId}`,
        }));
        setAdAssetViewItems(itemsWithId);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        enqueueSnackbar('Failed to fetch ad asset view data', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (campaignId && adGroupId && customerId) {
      fetchAdAssetView(customerId, campaignId, adGroupId);
    }
  }, [customerId, campaignId, adGroupId, fetchAdAssetView]);

  const columns: Array<GridColDef> = [
    {
      field: 'adGroupAdId',
      headerName: 'Ad Id',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'fieldType',
      headerName: 'Asset Type',
      headerAlign: 'center',
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value === 'HEADLINE' ? 'Headline' : 'Description'}
          color={params.value === 'HEADLINE' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'textAsset',
      headerName: 'Copy',
      flex: 4,
      renderCell: (params) => (
        <Stack spacing={0.5}>
          <Typography variant="body2">{params.value}</Typography>
          <Typography variant="caption" color="text.secondary">
            Impressions {params.row.impressions} • Clicks {params.row.clicks} • Average CPC {params.row.averageCpc} •
            Conversions {params.row.allConversions}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'relativePerformance',
      headerName: 'Performance vs. Group',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => (
        <Stack spacing={0.5} alignItems="flex-end">
          <Typography color={params.value > 105 ? 'success.main' : params.value < 90 ? 'error.main' : 'text.primary'}>
            {params.row.ctr.toFixed(2)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Score {((params.row.ctr * 100) / adGroup.ctr).toFixed(0)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'allConversions',
      headerName: 'TTV',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => Number(value).toLocaleString(),
    },
    {
      field: 'cost',
      headerName: 'Spend',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => `$${Number(value || 0).toLocaleString()}`,
    },
    {
      field: 'ctr',
      headerName: 'CTR',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => formatPercent(value || 0),
    },
    {
      field: 'ai',
      headerName: 'Replace with AI',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          color="primary"
          checked={aiSelections.has(params.row.id)}
          onChange={(event) => {
            const updated = new Set(aiSelections);
            if (event.target.checked) {
              updated.add(params.row.id);
            } else {
              updated.delete(params.row.id);
            }
            setAiSelections(updated);
          }}
          inputProps={{ 'aria-label': `Mark ${params.row.text} for AI replacement` }}
        />
      ),
    },
  ];

  if (!customerId || !campaignId || !adGroupId) {
    return null;
  }

  if (fetchingState === 'loading') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  return (
    <Paper>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              Campaign:
            </Typography>

            <Chip
              key={campaignId}
              label={`${adAssetViewItems?.length > 0 ? adAssetViewItems[0].campaignName : ''}`}
              color="primary"
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              Ad Group:
            </Typography>

            <Chip
              key={campaignId}
              label={`${adAssetViewItems?.length > 0 ? adAssetViewItems[0].adGroupName : ''}`}
              color="secondary"
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Typography variant="subtitle2" color="text.secondary">
              Marked for AI:
            </Typography>
            <Chip color="primary" label={aiSelections.size} />
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Impressions
              </Typography>
              <Typography variant="h5">
                {adGroup?.impressions ? Number(adGroup.impressions).toLocaleString() : '0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total views
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Clicks
              </Typography>
              <Typography variant="h5">{adGroup?.clicks ? Number(adGroup.clicks).toLocaleString() : '0'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total Clicks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                CTR
              </Typography>
              <Typography variant="h5">{adGroup?.ctr ? formatPercent(Number(adGroup.ctr)) : 'N/A'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Across selected ad group
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Spend
              </Typography>
              <Typography variant="h5">${adGroup?.spend ? Number(adGroup.spend).toLocaleString() : '0'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Combined outlay
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select campaigns to pull every headline and description for their ad groups. Assets are ranked against peers
            in the same ad group using CTR, spend, and total transaction value (TTV). Toggle any rows you want AI to
            replace and generate new copy based on the best performers. Metrics shown for last 2 weeks.
          </Alert>
        </Grid>
        <Grid item xs={12} md={4} textAlign="right">
          <Button variant="contained" color="primary" sx={{ mr: 1 }}>
            Generate AI Replacements
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => setAiSelections(new Set())}>
            Clear Selections
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <DataGrid
          rows={adAssetViewItems}
          getRowId={(row) => row.id}
          columns={columns}
          autoHeight={false}
          checkboxSelection={false}
          density="comfortable"
          disableRowSelectionOnClick
          slots={{ pagination: GridPagination }}
          paginationMode="client"
        />
      </Box>
    </Paper>
  );
}
